/**
 * Helpers for the notification details drawer: rendered HTML preview and raw JSON display.
 *
 * Preview rules:
 * - Body source: `details.emailTemplate ?? details.message`.
 * - Applies `{{break}}` → `<br/>` and `**text**` → `<strong>` on trusted template strings.
 * - When only `message` is used (no `emailTemplate`), prepends `details.subject` as an `<h2>` if both apply.
 * - When `details.emailTemplate` is set, parses that HTML without a subject heading in the body.
 * - `details.type === "PHYSICAL"` or `"CORRESPONDENCE"` → `physicalUnavailable` (no HTML body in panel).
 *
 * @typedef {'physicalUnavailable' | 'empty' | 'html'} PreviewVariant
 */

/**
 * Replaces template markers in preview body text/HTML before parsing or injection.
 *
 * @param {string} html Raw content from the details API (may include `{{break}}` or markdown-style `**`).
 * @returns {string} Markup with breaks and emphasis tags applied.
 */
function applyPreviewTransforms(html) {
    let out = String(html);
    out = out.replace(/\{\{break\}\}/g, "<br/>");
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    return out;
}

/**
 * Builds the model used by the “Rendered preview” tab from `GET /notifications/details` payload fields.
 *
 * @param {Record<string, unknown> | undefined} details Response body from the notification details API (`fetchNotificationDetails` in `apiUtils`).
 * @returns {{
 *   variant: PreviewVariant,
 *   htmlString?: string,
 *   message?: string
 * }}
 */
export function buildRenderedPreviewModel(details) {
    if (details.type === "PHYSICAL" || details.type === "CORRESPONDENCE") {
        return { variant: "physicalUnavailable" };
    }

    const emailTemplate = details.emailTemplate;
    const message = details.message;
    const content = emailTemplate ?? message;

    if (!content) {
        return { variant: "empty" };
    }

    const prependSubjectHeading = Boolean(message) && !Boolean(emailTemplate);

    const subject = !details.subject ? "" : String(details.subject);

    return htmlVariant(String(content), {
        prependSubjectHeading,
        subject,
    });
}

/**
 * Parses preview HTML and optionally prepends a subject heading for in-app-style messages.
 *
 * @param {string} htmlOrFragment Body HTML after {@link applyPreviewTransforms}.
 * @param {{ prependSubjectHeading: boolean, subject: string }} opts When `prependSubjectHeading` is true, inserts `<h2 class="notification-details-sheet__preview-subject">` with `subject`.
 * @returns {{ variant: 'html', htmlString: string }}
 */
function htmlVariant(htmlOrFragment, opts) {
    const { prependSubjectHeading, subject } = opts;
    const prepared = applyPreviewTransforms(String(htmlOrFragment));

    if (typeof document === "undefined") {
        const inner = prependSubjectHeading
            ? `<h2 class="notification-details-sheet__preview-subject">${escapeHtml(
                  subject,
              )}</h2>${prepared}`
            : prepared;

        return {
            variant: "html",
            htmlString: `<div class="notification-details-sheet__preview-fallback">${inner}</div>`,
        };
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(prepared, "text/html");

    if (prependSubjectHeading) {
        const h2 = doc.createElement("h2");
        h2.textContent = subject;
        h2.className = "notification-details-sheet__preview-subject";
        doc.body.prepend(h2);
    }

    return { variant: "html", htmlString: doc.body.innerHTML };
}

/**
 * Escapes plain text for safe concatenation into HTML when `document` is unavailable (SSR fallback).
 *
 * @param {string} s Unescaped text.
 * @returns {string} Entity-escaped text.
 */
function escapeHtml(s) {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/**
 * Shallow-clones notification details for `react-json-view`. Long `emailTemplate` values are truncated
 * (first 300 characters plus `…`) so the tree stays readable.
 *
 * @param {Record<string, unknown> | undefined} details Raw details from the API.
 * @returns {Record<string, unknown> | undefined} Clone safe to mutate for display.
 */
export function formatDetailsForRawJsonView(details) {
    const copy = { ...details };

    if (copy.emailTemplate) {
        copy.emailTemplate = `${String(copy.emailTemplate).slice(0, 300)}...`;
    }

    return copy;
}
